import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  SuiDialogConfig,
  DialogCloseReason,
  SuiDialogBase,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
} from '@sui/dialog';
import { SuiInputComponent } from '@sui/input';
import {
  matchPaletteEntries,
  normalizeForSearch,
  PaletteEntry,
  PaletteMatch,
} from './command-palette.utils';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { HighlightPipe } from '../../../shared/pipes/highlight.pipe';

export interface CommandPaletteData {
  entries: PaletteEntry[];
  /** Recently opened pages, newest first, already resolved to live entries. */
  recent: PaletteEntry[];
}

/** One titled block of the list; `offset` is the flat index of its first row. */
interface PaletteGroup {
  titleKey: string;
  rows: PaletteMatch[];
  offset: number;
}

/** Rows shown for a non-empty query; an empty query lists everything. */
const RESULT_LIMIT = 15;

let paletteCounter = 0;

/**
 * Command palette (WAP-1692): a single search box over the static routes of
 * every app area, opened with Ctrl+/ or the header button by Global Admins and
 * above. Rows read `Area -> Section -> Item`; arrows move through the recent
 * and the full list as one, Enter opens the highlighted route, Esc and the
 * backdrop close. The dialog closes with the chosen entry - navigation and the
 * recent list belong to CommandPaletteService. The entries arrive already
 * filtered by the caller's access level and feature components, so the list
 * never offers what the main menu hides.
 */
@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    SuiInputComponent,
    FormsModule,
    TranslatePipe,
    HighlightPipe,
  ],
})
export class CommandPaletteComponent
  extends SuiDialogBase<CommandPaletteData, PaletteEntry>
  implements AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 640 };
  @ViewChild('searchInput') private searchInput?: SuiInputComponent;
  @ViewChildren('option') private options?: QueryList<ElementRef<HTMLElement>>;

  protected readonly listId = `command-palette-list-${++paletteCounter}`;
  protected readonly query = signal('');
  protected readonly activeIndex = signal(0);

  protected readonly groups = computed<PaletteGroup[]>(() => {
    const query = this.query();
    const matches = matchPaletteEntries(
      this.dialogData.entries,
      query,
      RESULT_LIMIT,
    );
    if (normalizeForSearch(query)) {
      return [
        {
          titleKey: 'command-palette.group.matching-pages',
          rows: matches,
          offset: 0,
        },
      ];
    }
    const recent = this.dialogData.recent.map((e) => ({ ...e, rank: 0 }));
    const groups: PaletteGroup[] = [];
    if (recent.length) {
      groups.push({
        titleKey: 'command-palette.group.recent-pages',
        rows: recent,
        offset: 0,
      });
    }
    groups.push({
      titleKey: 'command-palette.group.all-pages',
      rows: matches,
      offset: recent.length,
    });
    return groups;
  });

  protected readonly rows = computed<PaletteMatch[]>(() =>
    this.groups().flatMap((g) => g.rows),
  );

  ngAfterViewInit(): void {
    // The dialog animates in; focus once the input is attached and visible.
    setTimeout(() => this.searchInput?.focus());
  }

  protected optionId(index: number): string {
    return `${this.listId}-option-${index}`;
  }

  protected onQuery(value: string): void {
    this.query.set(value ?? '');
    this.activeIndex.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const count = this.rows().length;
    switch (event.key) {
      case 'ArrowDown':
        if (count) this.moveTo((this.activeIndex() + 1) % count);
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (count) this.moveTo((this.activeIndex() - 1 + count) % count);
        event.preventDefault();
        break;
      case 'Home':
        if (count) this.moveTo(0);
        event.preventDefault();
        break;
      case 'End':
        if (count) this.moveTo(count - 1);
        event.preventDefault();
        break;
      case 'Enter': {
        const entry = this.rows()[this.activeIndex()];
        if (entry) this.go(entry);
        event.preventDefault();
        break;
      }
      default:
        break;
    }
  }

  protected hover(index: number): void {
    this.activeIndex.set(index);
  }

  protected go(entry: PaletteEntry): void {
    this.close({ reason: DialogCloseReason.Confirm, data: entry });
  }

  protected cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  private moveTo(index: number): void {
    this.activeIndex.set(index);
    // Scroll after the active class has been painted on the new row.
    setTimeout(() =>
      this.options
        ?.get(index)
        ?.nativeElement.scrollIntoView({ block: 'nearest' }),
    );
  }
}
